/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
/**
 * 3D Force-Directed Graph component for A-Frame.
 */

import ThreeForceGraph from "three-forcegraph";

const AFRAME = window.AFRAME;

const parseJson = function (prop) {
    return typeof prop === "string" ? JSON.parse(prop) : prop; // already parsed
};

const parseFn = function (prop) {
    if (typeof prop === "function") return prop; // already a function
    return null;
};

const parseAccessor = function (prop) {
    if (!isNaN(parseFloat(prop))) {
        return parseFloat(prop);
    } // parse numbers
    if (parseFn(prop)) {
        return parseFn(prop);
    } // parse functions
    return prop; // strings
};

if (!AFRAME.components.forcegraph)
    AFRAME.registerComponent("forcegraph", {
        schema: {
            jsonUrl: { type: "string", default: "" },
            nodes: { default: [] },
            links: { parse: parseJson, default: [] },
            numDimensions: { type: "number", default: 3 },
            dagMode: { type: "string", default: "" },
            dagLevelDistance: { type: "number", default: 0 },
            nodeRelSize: { type: "number", default: 4 }, // volume per val unit
            nodeId: { type: "string", default: "id" },
            nodeLabel: { parse: parseAccessor, default: "name" },
            nodeDesc: { parse: parseAccessor, default: "desc" },
            nodeVal: { parse: parseAccessor, default: "val" },
            nodeResolution: { type: "number", default: 8 }, // how many slice segments in the sphere's circumference
            nodeVisibility: { parse: parseAccessor, default: true },
            nodeColor: { parse: parseAccessor, default: "color" },
            nodeAutoColorBy: { parse: parseAccessor, default: "" }, // color nodes with the same field equally
            nodeOpacity: { type: "number", default: 0.75 },
            nodeThreeObject: { parse: parseAccessor, default: false },
            nodeThreeObjectExtend: { parse: parseAccessor, default: false },
            linkSource: { type: "string", default: "source" },
            linkTarget: { type: "string", default: "target" },
            linkLabel: { parse: parseAccessor, default: "name" },
            linkDesc: { parse: parseAccessor, default: "desc" },
            linkHoverPrecision: { type: "number", default: 2 },
            linkVisibility: { parse: parseAccessor, default: true },
            linkColor: { parse: parseAccessor, default: "color" },
            linkAutoColorBy: { parse: parseAccessor, default: "" }, // color links with the same field equally
            linkOpacity: { type: "number", default: 0.2 },
            linkWidth: { parse: parseAccessor, default: 0 },
            linkResolution: { type: "number", default: 6 }, // how many radial segments in each line cylinder's geometry
            linkCurvature: { parse: parseAccessor, default: 0 },
            linkCurveRotation: { parse: parseAccessor, default: 0 },
            linkMaterial: { parse: parseAccessor, default: null },
            linkThreeObject: { parse: parseAccessor, default: null },
            linkThreeObjectExtend: { parse: parseAccessor, default: false },
            linkPositionUpdate: { parse: parseFn, default: null },
            linkDirectionalArrowLength: { parse: parseAccessor, default: 0 },
            linkDirectionalArrowColor: { parse: parseAccessor, default: null },
            linkDirectionalArrowRelPos: { parse: parseAccessor, default: 0.5 }, // value between 0<>1 indicating the relative pos along the (exposed) line
            linkDirectionalArrowResolution: { type: "number", default: 8 }, // how many slice segments in the arrow's conic circumference
            linkDirectionalParticles: { parse: parseAccessor, default: 0 }, // animate photons travelling in the link direction
            linkDirectionalParticleSpeed: {
                parse: parseAccessor,
                default: 0.01
            }, // in link length ratio per frame
            linkDirectionalParticleWidth: {
                parse: parseAccessor,
                default: 0.5
            },
            linkDirectionalParticleColor: {
                parse: parseAccessor,
                default: null
            },
            linkDirectionalParticleResolution: { type: "number", default: 4 }, // how many slice segments in the particle sphere's circumference
            onNodeCenterHover: { parse: parseFn, default: function () {} },
            onLinkCenterHover: { parse: parseFn, default: function () {} },
            forceEngine: { type: "string", default: "d3" }, // 'd3' or 'ngraph'
            d3AlphaDecay: { type: "number", default: 0.0228 },
            d3VelocityDecay: { type: "number", default: 0.4 },
            warmupTicks: { type: "int", default: 0 }, // how many times to tick the force engine at init before starting to render
            cooldownTicks: { type: "int", default: 1e18 }, // Simulate infinity (int parser doesn't accept Infinity object)
            cooldownTime: { type: "int", default: 15000 }, // ms
            onEngineTick: { parse: parseFn, default: function () {} },
            onEngineStop: { parse: parseFn, default: function () {} }
        },

        emitParticle: function () {
            if (!this.forceGraph) {
                // Got here before component init -> initialize forceGraph
                this.forceGraph = new ThreeForceGraph();
            }

            const forceGraph = this.forceGraph;
            const returnVal = forceGraph.emitParticle.apply(
                forceGraph,
                arguments
            );

            return returnVal === forceGraph
                ? this // return self, not the inner forcegraph component
                : returnVal;
        },

        d3Force: function () {
            if (!this.forceGraph) {
                // Got here before component init -> initialize forceGraph
                this.forceGraph = new ThreeForceGraph();
            }

            const forceGraph = this.forceGraph;
            const returnVal = forceGraph.d3Force.apply(forceGraph, arguments);

            return returnVal === forceGraph
                ? this // return self, not the inner forcegraph component
                : returnVal;
        },

        d3ReheatSimulation: function () {
            this.forceGraph && this.forceGraph.d3ReheatSimulation();
            return this;
        },

        refresh: function () {
            this.forceGraph && this.forceGraph.refresh();
            return this;
        },

        init: function () {
            if (!this.forceGraph) this.forceGraph = new ThreeForceGraph();
            this.forceGraph.nodeThreeObject(this.data.nodeThreeObject);

            // initialize forceGraph if it doesn't exist yet
            this.el.object3D.add(this.forceGraph);

            const elData = this.data;

            // const diff = AFRAME.utils.diff(elData, oldData);

            const fgProps = [
                "jsonUrl",
                "numDimensions",
                "dagMode",
                "dagLevelDistance",
                "nodeRelSize",
                "nodeId",
                "nodeVal",
                "nodeResolution",
                "nodeVisibility",
                "nodeColor",
                "nodeAutoColorBy",
                "nodeOpacity",
                "nodeThreeObjectExtend",
                "linkSource",
                "linkTarget",
                "linkVisibility",
                "linkColor",
                "linkAutoColorBy",
                "linkOpacity",
                "linkWidth",
                "linkResolution",
                "linkCurvature",
                "linkCurveRotation",
                "linkMaterial",
                "linkThreeObject",
                "linkThreeObjectExtend",
                "linkPositionUpdate",
                "linkDirectionalArrowLength",
                "linkDirectionalArrowColor",
                "linkDirectionalArrowRelPos",
                "linkDirectionalArrowResolution",
                "linkDirectionalParticles",
                "linkDirectionalParticleSpeed",
                "linkDirectionalParticleWidth",
                "linkDirectionalParticleColor",
                "linkDirectionalParticleResolution",
                "forceEngine",
                "d3AlphaDecay",
                "d3VelocityDecay",
                "warmupTicks",
                "cooldownTicks",
                "cooldownTime",
                "onEngineTick",
                "onEngineStop"
            ];

            fgProps.forEach((p) => {
                this.forceGraph[p](elData[p] !== "" ? elData[p] : null);
            }); // Convert blank values into nulls

            this.localNodesData = {
                nodes: elData.nodes.map((x) => ({ ...x })),
                links: elData.links.map((x) => ({ ...x }))
            };
            this.forceGraph.graphData(this.localNodesData);
        },

        update: function (oldData) {
            if (!oldData) return;
            const elData = this.data;
            let reload = false;
            if (!AFRAME.utils.deepEqual(elData.nodes, oldData.nodes)) {
                if (
                    oldData.nodes &&
                    elData.nodes.length === oldData.nodes.length
                )
                    this.localNodesData.nodes.forEach((x, id) => {
                        Object.assign(x, elData.nodes[id]);
                    });
                else this.localNodesData.nodes = elData.nodes;

                reload = true;
            }
            if (!AFRAME.utils.deepEqual(elData.links, oldData.links)) {
                if (
                    oldData.links &&
                    elData.links.length === oldData.links.length
                )
                    this.localNodesData.links.forEach((x, id) => {
                        Object.assign(x, elData.links[id]);
                    });
                else this.localNodesData.links = elData.links;
                reload = true;
            }
            if (reload) {
                this.forceGraph.graphData(this.localNodesData);
                this.el.innerHTML = "";
            }
        },

        tick: function (t, td) {
            // Run force-graph ticker
            this.forceGraph.tickFrame();
        }
    });
